/**
 * Injects <meta name="facebook-domain-verification" content="..."> into <head>
 * by fetching the token from the meta-domain-config edge function (which reads
 * the META_DOMAIN_VERIFICATION secret server-side).
 *
 * Renders nothing. Safe no-op if the secret is not set.
 */
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export default function MetaDomainVerification() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke<{
          token: string | null;
        }>("meta-domain-config");
        if (cancelled || error) return;
        const raw = (data?.token ?? "").toString().trim();
        if (!raw) return;
        // Be tolerant: accept either a bare token or a full <meta ... content="..."> tag.
        let token = raw;
        const m = raw.match(/content\s*=\s*["']([^"']+)["']/i);
        if (m) token = m[1].trim();
        // Strip stray quotes
        token = token.replace(/^["']|["']$/g, "").trim();
        if (!token || /[<>]/.test(token)) return;
        const existing = document.querySelector(
          'meta[name="facebook-domain-verification"]',
        );
        if (existing) {
          existing.setAttribute("content", token);
          return;
        }
        const meta = document.createElement("meta");
        meta.setAttribute("name", "facebook-domain-verification");
        meta.setAttribute("content", token);
        document.head.appendChild(meta);
      } catch {
        /* never block UX */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
